import { type ExternalAccounts, type LinkedAccounts } from '../account/account'
import { type Address, type Name, type PhoneNumber } from '../common/common'

export interface UserAttributes {
  firstDeposit?: boolean
}

export interface CustomerServiceTokens {
  helpCenterToken: string
  inAppChatToken: string
}

export interface CustomerServiceOutput {
  tokens: CustomerServiceTokens
}

export enum EmploymentStatus {
  UNEMPLOYED = 'UNEMPLOYED',
  STUDENT = 'STUDENT',
  RETIRED = 'RETIRED',
  INDEPENDENT = 'INDEPENDENT',
  EMPLOYED = 'EMPLOYED',
}

export enum Occupation {
  EDUCATION = 'EDUCATION',
  ENGINEERING_SCIENCES = 'ENGINEERING_SCIENCES',
  FINANCE = 'FINANCE',
  HEALTHCARE = 'HEALTHCARE',
  LEGAL = 'LEGAL',
  MARKETING_ADVERTISING = 'MARKETING_ADVERTISING',
  PROFESSIONAL_SERVICES = 'PROFESSIONAL_SERVICES',
  SALES = 'SALES',
  TECHNOLOGY_IT = 'TECHNOLOGY_IT',
  OTHER = 'OTHER',
}

export enum PersonalNetWorthRange {
  BELOW_100K = 'BELOW_100K',
  BETWEEN_100K_AND_200K = 'BETWEEN_100K_AND_200K',
  BETWEEN_200K_AND_500K = 'BETWEEN_200K_AND_500K',
  BETWEEN_500K_AND_1M = 'BETWEEN_500K_AND_1M',
  BETWEEN_1M_AND_2M = 'BETWEEN_1M_AND_2M',
  BETWEEN_2M_AND_5M = 'BETWEEN_2M_AND_5M',
  OVER_5M = 'OVER_5M',
}

export enum EnrollmentTaxFilingStatusType {
  SINGLE = 'SINGLE',
  MARRIED_FILING_JOINTLY = 'MARRIED_FILING_JOINTLY',
  MARRIED_FILING_SEPARATELY = 'MARRIED_FILING_SEPARATELY',
  HEAD_OF_HOUSEHOLD = 'HEAD_OF_HOUSEHOLD',
}

export enum EnrollmentInvestmentObjectiveCategory {
  GENERATE_INCOME = 'GENERATE_INCOME',
  SPECIFIC_GOAL = 'SPECIFIC_GOAL',
  SAVE_FOR_RETIREMENT = 'SAVE_FOR_RETIREMENT',
  BUILD_WEALTH = 'BUILD_WEALTH',
  CASH_MANAGEMENT = 'CASH_MANAGEMENT',
}

export enum EnrollmentInvestmentExperienceCategory {
  NONE = 'NONE',
  VERY_LITTLE = 'VERY_LITTLE',
  SOME = 'SOME',
  MODERATE = 'MODERATE',
  A_LOT = 'A_LOT',
}

export interface Customer {
  name: Name
  address: Address
  phone: PhoneNumber
  email: string
  linkedAccounts: LinkedAccounts
  externalAccounts: ExternalAccounts
  ssnLastFour: string
  birthDate: string
  tags?: string[]
  attributes: UserAttributes
  lastLogin?: string
  createdDate: string
  // Employment Info
  employmentStatus?: EmploymentStatus
  occupation?: Occupation
  // Financial Info
  personalAnnualGrossIncome: number
  personalNetWorthRange: PersonalNetWorthRange
  maritalStatus: MaritalStatus
  // Suitability
  isBrokerDealer?: boolean
  brokerDealerEmployer?: string
  isDirectorPolicyMakerOfCompany?: boolean
  directorPolicyMakerOfCompany?: string
  taxFilingStatus?: EnrollmentTaxFilingStatusType
  investmentObjective?: EnrollmentInvestmentObjectiveCategory
  investmentExperience?: EnrollmentInvestmentExperienceCategory
  isPoliticallyExposedPerson?: boolean
  affiliatedPoliticalOrganizations?: string[]
  hasPoliticallyExposedImmediateFamilyMembers?: boolean
  politicallyExposedImmediateFamilyMembers?: string[]
}

export interface CustomerDetails {
  customer: Customer
  customerService: CustomerServiceOutput
}

export enum MaritalStatus {
  SINGLE = 'SINGLE',
  MARRIED = 'MARRIED',
  DIVORCED = 'DIVORCED',
  WIDOWED = 'WIDOWED',
}
